import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaAngleRight, FaStar, FaRegStar, FaLock, FaUnlock, FaChevronUp, FaChevronDown, FaEdit, FaHome } from "react-icons/fa";
import U2000SelectOrganizationDialog from "../../components/dialog/U2000SelectOrganizationDialog";
import U2000DefaultScreenModal from "../../components/dialog/U2000DefaultScreenModal";
import { useU2000AuthContext } from "../../context/auth/U2000UseAuthContext";
import { useU2000FavoriteMenuHandlers } from "../../hooks/useU2000FavoriteMenuHandlers";
import { useU2000MenuStateHandlers } from "../../hooks/useU2000MenuStateHandlers";
import { u2000OpenAppRouteInNewTab } from "../../tools/u2000OpenAppRouteInNewTab";
import {
  U2000GetVersionNotesPath,
  U2000GetVersionReleasedAtYmd,
  U2000GetVersionWithPrefix,
  U2000IsVersionNew,
} from "../../tools/U2000GetVersion";
import type { U2000MenuModule } from "../../menu/U2000Menu";
import type { U2000MenuItem } from "../../menu/U2000MenuUtils";

interface U2000VerticalMenuProps {
  menuData: U2000MenuModule[];
  className?: string;
}

interface MenuItemProps {
  item: U2000MenuItem;
  level?: number;
  isCollapsed: boolean;
  onItemClick?: (item: U2000MenuItem) => void;
  favoriteHandlers: ReturnType<typeof useU2000FavoriteMenuHandlers>;
}

/** Permission item : rôles + org session si `requiresOrganizationId` (BL-0339). */
function u2000MenuItemHasPermission(
  menuItem: U2000MenuItem,
  hasRole: (role: string) => boolean,
  hasOrganizationId: boolean,
): boolean {
  const roleOk =
    !menuItem.allowedRoles ||
    (menuItem.allowedRoles.length > 0 && menuItem.allowedRoles.some((role: string) => hasRole(role)));
  if (!roleOk) {
    return false;
  }
  if (menuItem.requiresOrganizationId && !hasOrganizationId) {
    return false;
  }
  return true;
}

/**
 * BL-0342 — parent visible seulement s’il reste une feuille autorisée
 * à n’importe quelle profondeur (aligné sur updateMenu récursif).
 */
function u2000MenuItemHasAccessibleLeaf(
  menuItem: U2000MenuItem,
  hasRole: (role: string) => boolean,
  hasOrganizationId: boolean,
): boolean {
  if (!u2000MenuItemHasPermission(menuItem, hasRole, hasOrganizationId)) {
    return false;
  }
  const children = menuItem.menuItems;
  if (children && children.length > 0) {
    return children.some((child) => u2000MenuItemHasAccessibleLeaf(child, hasRole, hasOrganizationId));
  }
  return Boolean(menuItem.link);
}

const MenuItem: React.FC<MenuItemProps> = ({ item, level = 0, isCollapsed, onItemClick, favoriteHandlers }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, hasRole } = useU2000AuthContext();
  const hasOrganizationId = Boolean(auth?.user?.organizationId);

  // Check if user has permission for this menu item
  const hasPermission = u2000MenuItemHasPermission(item, hasRole, hasOrganizationId);

  if (!hasPermission) {
    return null;
  }

  const hasSubItems = item.menuItems && item.menuItems.length > 0;

  // BL-0342 — filtre récursif : dossier sans feuille autorisée → masqué
  if (hasSubItems) {
    const hasAccessibleSubItems = item.menuItems!.some((subItem) =>
      u2000MenuItemHasAccessibleLeaf(subItem, hasRole, hasOrganizationId),
    );

    if (!hasAccessibleSubItems) {
      return null;
    }
  }

  // Don't display menu items that don't have sub-items (unless they have a direct link)
  if (!hasSubItems && !item.link) {
    return null;
  }

  const isActive = item.link && location.pathname.startsWith(item.link);
  const isFavorite = item.link ? favoriteHandlers.isFavorite(item.link) : false;
  const showFavoriteStar = item.link && !isCollapsed; // Only show star for items with links

  // Calculate padding for indentation
  const paddingLeft = isCollapsed ? 12 : level === 0 ? 16 : 16 + level * 24;

  const handleClick = () => {
    if (hasSubItems) {
      setIsExpanded(!isExpanded);
    } else if (item.link) {
      favoriteHandlers.trackUsage(item.link); // Track usage when navigating
      navigate(item.link);
      onItemClick?.(item);
    }
  };

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent menu navigation
    if (item.link && item.label) {
      favoriteHandlers.toggleFavorite(item.link, t(item.label) || item.label, item.icon?.toString(), level === 0 ? "Main Menu" : "Submenu");
    }
  };

  return (
    <div className="w-full min-w-0">
      <div
        onClick={handleClick}
        style={!isCollapsed ? { paddingLeft: `${paddingLeft}px` } : {}}
        className={`
          ${
            isCollapsed && level === 0
              ? "flex items-center justify-center cursor-pointer transition-colors duration-200 py-3"
              : "flex items-center justify-between gap-1 cursor-pointer transition-colors duration-200 pr-3 py-3 min-w-0 overflow-hidden"
          }
          ${isActive ? "bg-blue-600 text-white" : "text-gray-300 hover:bg-gray-700 hover:text-white"}
          ${level > 0 ? "border-l-2 border-blue-500 bg-gray-800" : ""}
        `}
        title={(!isCollapsed || level > 0) && item.label ? t(item.label) : undefined}
      >
        {isCollapsed && level === 0 ? (
          /* Collapsed mode - center icon only */
          <div>{level === 0 && item.icon && <span className="shrink-0">{item.icon}</span>}</div>
        ) : (
          /* Expanded mode - min-w-0 + truncate évite le scroll horizontal (BL-0459) */
          <>
            <div className="flex items-center space-x-3 min-w-0 flex-1 overflow-hidden">
              {/* Icon */}
              {level === 0 && item.icon && <span className="shrink-0">{item.icon}</span>}

              {/* Label - only show when not collapsed or when showing submenu */}
              {(!isCollapsed || level > 0) && item.label && (
                <span className="text-sm font-medium truncate min-w-0">{t(item.label)}</span>
              )}
            </div>

            <div className="flex items-center space-x-2 shrink-0">
              {/* Favorite star - only for items with links */}
              {showFavoriteStar && (
                <button onClick={handleStarClick} className="p-1 rounded-sm hover:bg-gray-600 transition-colors">
                  {isFavorite ? <FaStar className="h-4 w-4 text-yellow-400" /> : <FaRegStar className="h-4 w-4 text-gray-400 hover:text-yellow-400" />}
                </button>
              )}

              {/* Expand/collapse arrow for items with subitems */}
              {hasSubItems && !isCollapsed && (
                <span className={`transform transition-transform duration-200 ${isExpanded ? "rotate-90" : "rotate-0"}`}>
                  <FaAngleRight className="h-4 w-4" />
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Submenu items */}
      {hasSubItems && isExpanded && !isCollapsed && (
        <div className="bg-gray-800">
          {item.menuItems!.map((subItem) => (
            <MenuItem key={subItem.id} item={subItem} level={level + 1} isCollapsed={false} onItemClick={onItemClick} favoriteHandlers={favoriteHandlers} />
          ))}
        </div>
      )}
    </div>
  );
};

const U2000VerticalMenu: React.FC<U2000VerticalMenuProps> = ({ menuData, className = "" }) => {
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const { auth } = useU2000AuthContext();
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [isDefaultScreenModalOpen, setIsDefaultScreenModalOpen] = useState(false);

  // Initialize menu state handlers with persistent state
  const menuStateHandlers = useU2000MenuStateHandlers({
    luName: "mainMenu",
  });

  // Initialize favorite handlers with a consistent luName for the main menu
  const favoriteHandlers = useU2000FavoriteMenuHandlers({
    luName: "mainMenu",
    settings: {
      maxFavorites: 20,
      autoCleanupDays: 90,
      groupByCategory: true,
      showUsageCount: true,
    },
  });

  // Extractors for readability
  const { isCollapsed, isPinned } = menuStateHandlers.menuState;
  const { setMenuCollapsed, setMenuState } = menuStateHandlers;

  // Version et notes (constantes au build time)
  const appVersion = useMemo(() => U2000GetVersionWithPrefix(), []);
  const versionNotesPath = useMemo(() => U2000GetVersionNotesPath(appVersion), [appVersion]);
  const isVersionNew = useMemo(() => U2000IsVersionNew(U2000GetVersionReleasedAtYmd()), []);
  const versionNotesLabel = t("lblVersionNotesLink", { version: appVersion });
  const versionNewLabel = t("lblVersionNewBadge");

  // Handle menu item click
  const handleMenuItemClick = (_item: U2000MenuItem) => {
    // Menu item click handled by navigation in MenuItem component
  };

  // Get favorite items for display
  const getFavoriteItems = () => {
    return favoriteHandlers.getFavoriteItems();
  };

  // Get current default screen as reactive state
  const [currentDefaultScreen, setCurrentDefaultScreen] = useState<string | null>(favoriteHandlers.getDefaultScreen());

  // Update currentDefaultScreen when localStorage settings change
  useEffect(() => {
    const updateDefaultScreen = () => {
      // Force reload of hook data
      favoriteHandlers.reloadData();

      // Lire directement depuis localStorage pour éviter les problèmes de timing
      const userSpecificKey = auth.isAuthenticated && auth.user.userName ? `mainMenu_${auth.user.userName}_${auth.user.organizationId || "noorg"}` : "mainMenu";
      const settingsKey = `${userSpecificKey}FavoriteMenuSettings`;
      const settingsData = localStorage.getItem(settingsKey);

      let defaultScreen: string | null = null;
      if (settingsData) {
        try {
          const settings = JSON.parse(settingsData);
          defaultScreen = settings.defaultScreenPath || null;
        } catch (error) {
          console.error("Error parsing settings from localStorage:", error);
        }
      }

      setCurrentDefaultScreen(defaultScreen);
    };

    // Update immediately
    updateDefaultScreen();

    // Listen for custom event from modal (BL-0337 : appliquer detail immédiatement)
    const handleDefaultScreenChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ newDefaultScreen?: string | null }>;
      const fromDetail = customEvent.detail?.newDefaultScreen;
      if (fromDetail !== undefined) {
        setCurrentDefaultScreen(fromDetail);
      }

      // Small delay to ensure localStorage is updated, then resync
      setTimeout(() => {
        updateDefaultScreen();
      }, 50);
    };

    window.addEventListener("defaultScreenChanged", handleDefaultScreenChanged);

    return () => {
      window.removeEventListener("defaultScreenChanged", handleDefaultScreenChanged);
    };
  }, [auth.user.organizationId]); // Écouter les changements d'organisation

  // Auto-collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setMenuCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Check initial size

    return () => window.removeEventListener("resize", handleResize);
  }, [setMenuCollapsed]);

  // Click outside to close hover expanded menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) && isHoverExpanded) {
        setIsHoverExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isHoverExpanded]);

  // Determine if menu should show as expanded (either not collapsed, pinned, or hover expanded)
  const showAsExpanded = !isCollapsed || isPinned || isHoverExpanded;
  const isOverlay = isCollapsed && !isPinned && isHoverExpanded;

  // Handle pin toggle - true toggle behavior
  const handlePinToggle = () => {
    if (isPinned) {
      // Unpinning: collapse the menu
      setMenuState({ isPinned: false, isCollapsed: true });
    } else {
      // Pinning: expand and pin the menu
      setMenuState({ isPinned: true, isCollapsed: false });
    }
  };

  const hasMultipleOrgs = !!(auth?.user?.organizations && auth.user.organizations.length > 1);
  const logoOpenNewTabHint = t("lblLogoOpenNewTabHint", { ns: "u2000" });
  const appRootPath = useMemo(() => {
    const base = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");
    return base ? `${base}/` : "/";
  }, []);

  const handleLogoIconClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      u2000OpenAppRouteInNewTab("/", { focus: true });
      return;
    }
    e.preventDefault();
  };

  const handleOrgNameClick = () => {
    if (hasMultipleOrgs) {
      setIsOrgDialogOpen(true);
    }
  };

  const logoIcon = (
    <div className="bg-green-600 p-1 rounded-lg">
      <img src="/assets/MonArpenteurWhite.svg" alt="MonArpenteur" className="w-8 h-8 rounded-lg object-contain" style={{ width: 30, height: 30, display: "block", maxWidth: "none" }} />
    </div>
  );

  const logoIconLink = (
    <a href={appRootPath} target="_blank" rel="noopener noreferrer" title={logoOpenNewTabHint} onClick={handleLogoIconClick} className="focus:outline-hidden cursor-pointer hover:opacity-90">
      {logoIcon}
    </a>
  );

  return (
    <div
      ref={menuRef}
      onMouseEnter={() => isCollapsed && !isPinned && setIsHoverExpanded(true)}
      onMouseLeave={() => isCollapsed && !isPinned && setIsHoverExpanded(false)}
      className={`
        ${showAsExpanded ? "w-64" : "w-16"} 
        ${isOverlay ? "fixed left-0 top-0 z-50 h-screen shadow-2xl" : "relative"}
        bg-gray-900 border-r border-gray-700 flex flex-col transition-all duration-300 ease-in-out min-w-0 overflow-hidden
        ${className}
      `}
    >
      {/* Header with logo and collapse button */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700">
        {showAsExpanded && (
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="shrink-0">{logoIconLink}</div>
            <button
              type="button"
              onClick={handleOrgNameClick}
              className={`flex-1 min-w-0 text-left focus:outline-hidden ${hasMultipleOrgs ? "cursor-pointer hover:opacity-90" : "cursor-default"}`}
            >
              <span className="text-white font-semibold text-sm block truncate min-w-0">
                {auth && auth.user && (auth.user.organizationName || auth.user.organizationId) ? auth.user.organizationName || auth.user.organizationId : "Administration"}
              </span>
            </button>
            {/* chevrons moved to the right side near the pin button */}
          </div>
        )}

        {!showAsExpanded && (
          <div className="w-full flex justify-center">{logoIconLink}</div>
        )}

        {/* Pin button - show when expanded */}
        {showAsExpanded && (
          <div className="flex items-center space-x-1 ml-2">
            {auth && auth.user && auth.user.organizations && auth.user.organizations.length > 1 && (
              <div className="flex flex-col items-center justify-center mr-1 space-y-0.5 text-gray-300">
                <FaChevronUp className="w-2 h-2" />
                <FaChevronDown className="w-2 h-2" />
              </div>
            )}
            {/* Pin button */}
            <button onClick={handlePinToggle} className="p-1 rounded-sm transition-colors text-white hover:text-gray-300" title={isPinned ? "Unpin menu" : "Pin menu"}>
              {isPinned ? <FaLock className="h-4 w-4" style={{ width: 14, height: 14 }} /> : <FaUnlock className="h-4 w-4" style={{ width: 14, height: 14 }} />}
            </button>
          </div>
        )}
      </div>

      <U2000SelectOrganizationDialog isOpen={isOrgDialogOpen} onClose={() => setIsOrgDialogOpen(false)} />

      <U2000DefaultScreenModal isOpen={isDefaultScreenModalOpen} onClose={() => setIsDefaultScreenModalOpen(false)} favoriteHandlers={favoriteHandlers} />

      {/* Favorites section */}
      {showAsExpanded && (
        <div className="px-4 py-3 border-b border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <div className="text-gray-500 text-xs uppercase tracking-wider">
              {t("favorites")} ({favoriteHandlers.itemCount})
            </div>
            <button onClick={() => setIsDefaultScreenModalOpen(true)} className="p-1 rounded-sm hover:bg-gray-600 transition-colors" title={t("Configure default screen")}>
              <FaEdit className="h-3 w-3 text-gray-400 hover:text-gray-300" />
            </button>
          </div>
          {favoriteHandlers.itemCount === 0 ? (
            <div className="text-gray-400 text-sm text-center py-4">
              <div className="mb-2">⭐ No favorites yet</div>
              <div className="text-xs text-gray-500">Click on the star on the right side of a menu item to add as a favorite.</div>
            </div>
          ) : (
            <div className="space-y-1">
              {getFavoriteItems().map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    favoriteHandlers.trackUsage(item.path);
                    navigate(item.path);
                  }}
                  className="flex items-center justify-between p-2 rounded-sm cursor-pointer text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    {currentDefaultScreen === item.path && <FaHome className="h-3 w-3 text-blue-400 shrink-0" title={t("Default screen")} />}
                    <span className="text-sm truncate">{item.name}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      favoriteHandlers.removeFavoriteItem(item.id);
                    }}
                    className="p-1 rounded-sm hover:bg-gray-600 transition-colors"
                  >
                    <FaStar className="h-4 w-4 text-yellow-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pages section — overflow-x-hidden : pas de scroll horizontal (libellés longs niveau 2+) */}
      <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
        {showAsExpanded && (
          <div className="px-4 py-3">
            <div className="text-gray-500 text-xs uppercase tracking-wider mb-2">{t("pages")}</div>
          </div>
        )}

        {/* Menu items */}
        <nav className="pb-4 min-w-0">
          {menuData.map((module) => (
            <MenuItem key={module.id} item={module} level={0} isCollapsed={!showAsExpanded} onItemClick={handleMenuItemClick} favoriteHandlers={favoriteHandlers} />
          ))}
        </nav>
      </div>

      {/* Version display */}
      {showAsExpanded && (
        <div className="border-t border-gray-700 p-4">
          <div className="flex items-center justify-center gap-2 text-sm">
            <a
              href={versionNotesPath}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white hover:underline transition-colors"
              title={versionNotesLabel}
              aria-label={versionNotesLabel}
            >
              {appVersion}
            </a>
            {isVersionNew && (
              <span
                className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-emerald-500 text-white"
                aria-label={versionNewLabel}
              >
                NEW
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default U2000VerticalMenu;
